import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings as SettingsIcon, User, Bell, Shield, Globe, 
  Palette, Database, Code, Key, Moon, Sun, Check,
  ChevronRight, Save, AlertCircle, Smartphone, Zap,
  Mail, Lock, Eye, EyeOff, Upload, Download, MessageCircle, Users, Plus
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import dbService from '../services/dbService';
import whatsappService from '../services/whatsappService';

interface SettingSection {
  id: string;
  title: string;
  icon: React.ElementType;
  description: string;
}

const Settings: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const [activeSection, setActiveSection] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    avatar: '',
  });

  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
    flowErrors: true,
    flowSuccess: false,
    updates: true,
  });
  
  // Carica i dati dell'utente dal database
  useEffect(() => {
    const user = dbService.getUser('user_1');
    if (user) {
      setProfile({
        name: user.name,
        email: user.email,
        phone: user.phone,
        company: user.company,
        avatar: user.avatar,
      });
      setNotifications(user.settings.notifications);
    }
  }, []);

  const [api, setApi] = useState({
    apiKey: 'waf_1234567890abcdef',
    webhookUrl: '',
    rateLimit: '100',
  });
  
  const [whatsappConfig, setWhatsappConfig] = useState<{
    apiType: 'cloud' | 'official' | 'web';
    apiUrl: string;
    accessToken: string;
    phoneNumberId: string;
    businessAccountId: string;
    apiKey: string;
    webhookVerifyToken: string;
  }>({
    apiType: 'cloud',
    apiUrl: '',
    accessToken: '',
    phoneNumberId: '',
    businessAccountId: '',
    apiKey: '',
    webhookVerifyToken: '',
  });
  
  // Carica configurazione WhatsApp
  useEffect(() => {
    const loadConfig = async () => {
      const config = await whatsappService.getConfig();
      if (config) {
        setWhatsappConfig(prev => ({ ...prev, ...config }));
      }
    };
    loadConfig();
  }, []);

  const sections: SettingSection[] = [
    { id: 'profile', title: 'Profilo', icon: User, description: 'Gestisci le tue informazioni personali' },
    { id: 'notifications', title: 'Notifiche', icon: Bell, description: 'Configura le preferenze di notifica' },
    { id: 'appearance', title: 'Aspetto', icon: Palette, description: 'Personalizza l\'interfaccia' },
    { id: 'api', title: 'API & Webhook', icon: Code, description: 'Gestisci integrazioni e API' },
    { id: 'conversations', title: 'Conversazioni', icon: MessageCircle, description: 'Gestisci partecipanti alle conversazioni' },
    { id: 'security', title: 'Sicurezza', icon: Shield, description: 'Impostazioni di sicurezza e privacy' },
    { id: 'data', title: 'Dati', icon: Database, description: 'Backup e gestione dati' },
  ];

  const handleSaveProfile = () => {
    const user = dbService.getUser('user_1');
    if (user) {
      dbService.updateUser('user_1', profile);
      toast.success('Profilo aggiornato con successo!');
    }
  };

  const handleSaveNotifications = () => {
    const user = dbService.getUser('user_1');
    if (user) {
      dbService.updateUser('user_1', {
        settings: { ...user.settings, notifications }
      });
      toast.success('Preferenze notifiche aggiornate!');
    }
  };

  const handleExportData = () => {
    const data = dbService.exportDatabase();
    const blob = new Blob([data], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whatsapp-flow-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Database esportato con successo!');
  };

  const handleImportData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const content = event.target?.result as string;
            if (dbService.importDatabase(content)) {
              toast.success('Database importato con successo!');
              window.location.reload();
            } else {
              toast.error('Errore nell\'importazione del database');
            }
          } catch (error) {
            toast.error('File non valido');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Informazioni Personali
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Avatar
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-whatsapp-green to-emerald-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {profile.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <button className="px-4 py-2 bg-gray-100 dark:bg-dark-bg text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors">
                      Cambia Avatar
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nome Completo
                    </label>
                    <input
                      type="text"
                      value={profile.name}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Telefono
                    </label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Azienda
                    </label>
                    <input
                      type="text"
                      value={profile.company}
                      onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                  </div>
                </div>
                
                <button
                  onClick={handleSaveProfile}
                  className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white rounded-lg hover:shadow-lg transition-all"
                >
                  <Save className="w-4 h-4" />
                  <span>Salva Modifiche</span>
                </button>
              </div>
            </div>
          </motion.div>
        );

      case 'notifications':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Preferenze Notifiche
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">Notifiche Email</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Ricevi aggiornamenti via email
                    </p>
                  </div>
                  <button
                    onClick={() => setNotifications({ ...notifications, email: !notifications.email })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      notifications.email ? 'bg-whatsapp-green' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                        notifications.email ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">Notifiche Push</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Notifiche nel browser
                    </p>
                  </div>
                  <button
                    onClick={() => setNotifications({ ...notifications, push: !notifications.push })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      notifications.push ? 'bg-whatsapp-green' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                        notifications.push ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">Errori Automazioni</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Notifica quando un'automazione fallisce
                    </p>
                  </div>
                  <button
                    onClick={() => setNotifications({ ...notifications, flowErrors: !notifications.flowErrors })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      notifications.flowErrors ? 'bg-whatsapp-green' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                        notifications.flowErrors ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">Aggiornamenti Prodotto</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Novità e funzionalità
                    </p>
                  </div>
                  <button
                    onClick={() => setNotifications({ ...notifications, updates: !notifications.updates })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      notifications.updates ? 'bg-whatsapp-green' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                        notifications.updates ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'appearance':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Personalizzazione Interfaccia
              </h3>
              <div className="space-y-4">
                <div className="p-6 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4">Tema</h4>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => !isDark && toggleTheme()}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                        !isDark
                          ? 'border-whatsapp-green bg-whatsapp-green/10'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      <Sun className="w-5 h-5" />
                      <span>Chiaro</span>
                      {!isDark && <Check className="w-4 h-4 text-whatsapp-green" />}
                    </button>
                    
                    <button
                      onClick={() => isDark && toggleTheme()}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                        isDark
                          ? 'border-whatsapp-green bg-whatsapp-green/10'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      <Moon className="w-5 h-5" />
                      <span>Scuro</span>
                      {isDark && <Check className="w-4 h-4 text-whatsapp-green" />}
                    </button>
                  </div>
                </div>

                <div className="p-6 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4">Lingua</h4>
                  <select className="w-full px-4 py-2 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50">
                    <option value="it">Italiano</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="fr">Français</option>
                    <option value="de">Deutsch</option>
                  </select>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'conversations':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Gestione Conversazioni Twilio
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Aggiungi il tuo numero WhatsApp alle conversazioni per ricevere i messaggi inoltrati dai flussi.
              </p>
              
              <div className="space-y-4">
                <div className="p-6 bg-gray-50 dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-dark-border">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Aggiungi Partecipante alle Conversazioni
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Numero WhatsApp (con prefisso internazionale)
                      </label>
                      <input
                        type="tel"
                        id="conversationPhone"
                        defaultValue="+38349253853"
                        placeholder="+393331234567"
                        className="w-full px-4 py-2 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Inserisci il numero WhatsApp che vuoi aggiungere alle conversazioni
                      </p>
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={async () => {
                          const phoneInput = document.getElementById('conversationPhone') as HTMLInputElement;
                          const phoneNumber = phoneInput?.value || '+38349253853';
                          
                          try {
                            // Use GET method (nginx allows GET, might block POST)
                            const encodedPhone = encodeURIComponent(phoneNumber);
                            const response = await fetch(`/conversations/add-participant?phoneNumber=${encodedPhone}`, {
                              method: 'GET',
                            });
                            
                            const result = await response.json();
                            
                            if (result.success) {
                              if (result.results?.problem?.success && result.results?.memo?.success) {
                                toast.success('Aggiunto a entrambe le conversazioni!');
                              } else if (result.results?.problem?.success || result.results?.memo?.success) {
                                toast.success('Aggiunto parzialmente alle conversazioni');
                              } else {
                                toast.error('Errore nell\'aggiunta alle conversazioni');
                              }
                            } else {
                              toast.error(result.error || 'Errore sconosciuto');
                            }
                          } catch (error: any) {
                            toast.error(`Errore: ${error.message}`);
                          }
                        }}
                        className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white rounded-lg hover:shadow-lg transition-all"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Aggiungi alle Conversazioni</span>
                      </button>
                    </div>
                    
                    <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex gap-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-1">Informazioni</h4>
                          <p className="text-sm text-blue-700 dark:text-blue-400">
                            Questo aggiungerà il numero specificato alle seguenti conversazioni:
                          </p>
                          <ul className="text-sm text-blue-700 dark:text-blue-400 mt-2 list-disc list-inside space-y-1">
                            <li><strong>Problemo Group</strong> - Riceverai i messaggi inoltrati da FLOW 1</li>
                            <li><strong>Memos</strong> - Riceverai i messaggi inoltrati da FLOW 2</li>
                          </ul>
                          <p className="text-sm text-blue-700 dark:text-blue-400 mt-2">
                            Dopo l'aggiunta, riceverai i messaggi direttamente su WhatsApp.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'api':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* WhatsApp API Configuration */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Configurazione WhatsApp API
              </h3>
              
              {/* API Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Tipo di API
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'cloud', label: 'WhatsApp Cloud API', desc: 'Ufficiale Facebook' },
                    { value: 'official', label: 'Business API', desc: 'On-premise' },
                    { value: 'web', label: 'Web API', desc: 'WhatsApp Web' }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setWhatsappConfig({ ...whatsappConfig, apiType: type.value as any });
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        whatsappConfig.apiType === type.value
                          ? 'border-whatsapp-green bg-whatsapp-green/10'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      <div className="font-medium text-gray-900 dark:text-white">
                        {type.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {type.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Cloud API Configuration */}
              {whatsappConfig.apiType === 'cloud' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Access Token
                    </label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={whatsappConfig.accessToken}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, accessToken: e.target.value })}
                      placeholder="EAABxxxxxxx..."
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone Number ID
                    </label>
                    <input
                      type="text"
                      value={whatsappConfig.phoneNumberId}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, phoneNumberId: e.target.value })}
                      placeholder="1234567890123456"
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Business Account ID
                    </label>
                    <input
                      type="text"
                      value={whatsappConfig.businessAccountId}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, businessAccountId: e.target.value })}
                      placeholder="1234567890123456"
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                  </div>
                </div>
              )}

              {/* Official/Web API Configuration */}
              {(whatsappConfig.apiType === 'official' || whatsappConfig.apiType === 'web') && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      API URL
                    </label>
                    <input
                      type="url"
                      value={whatsappConfig.apiUrl}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, apiUrl: e.target.value })}
                      placeholder="https://api.tuoserver.com"
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      API Key
                    </label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={whatsappConfig.apiKey}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, apiKey: e.target.value })}
                      placeholder="your-api-key"
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                  </div>
                </div>
              )}

              {/* Webhook Configuration */}
              <div className="mt-6 p-4 bg-gray-50 dark:bg-dark-bg rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                  Configurazione Webhook
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Webhook URL (configura questo URL su WhatsApp)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={`${window.location.origin}/api/webhook/whatsapp`}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded text-sm"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/api/webhook/whatsapp`);
                          toast.success('URL copiato!');
                        }}
                        className="px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        Copia
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Verify Token
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={whatsappConfig.webhookVerifyToken}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded text-sm font-mono"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(whatsappConfig.webhookVerifyToken);
                          toast.success('Token copiato!');
                        }}
                        className="px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        Copia
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={async () => {
                    await whatsappService.updateConfig({
                      problemGroup: whatsappConfig.businessAccountId,
                      memoGroup: whatsappConfig.phoneNumberId
                    });
                    toast.success('Configurazione salvata!');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white rounded-lg hover:shadow-lg transition-all"
                >
                  <Save className="w-4 h-4" />
                  <span>Salva Configurazione</span>
                </button>
                
                <button
                  onClick={async () => {
                    const result = await whatsappService.testConnection();
                    if (result) {
                      toast.success('Connessione riuscita!');
                    } else {
                      toast.error('Connessione fallita. Verifica le credenziali.');
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Zap className="w-4 h-4" />
                  <span>Test Connessione</span>
                </button>
              </div>

              {/* Documentation */}
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 dark:text-blue-300">Guida Configurazione</h4>
                    <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                      Per configurare WhatsApp Cloud API, devi prima creare un'app su Facebook Developers
                      e ottenere le credenziali necessarie.
                    </p>
                    <div className="mt-2 space-y-1">
                      <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" 
                         target="_blank" 
                         rel="noopener noreferrer"
                         className="text-sm text-blue-600 dark:text-blue-400 underline block">
                        → Guida WhatsApp Cloud API
                      </a>
                      <a href="https://business.whatsapp.com/" 
                         target="_blank" 
                         rel="noopener noreferrer"
                         className="text-sm text-blue-600 dark:text-blue-400 underline block">
                        → WhatsApp Business Platform
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'security':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Sicurezza Account
              </h3>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Cambia Password</h4>
                  <div className="space-y-3">
                    <input
                      type="password"
                      placeholder="Password attuale"
                      className="w-full px-4 py-2 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                    <input
                      type="password"
                      placeholder="Nuova password"
                      className="w-full px-4 py-2 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                    <input
                      type="password"
                      placeholder="Conferma nuova password"
                      className="w-full px-4 py-2 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                    />
                    <button className="px-4 py-2 bg-whatsapp-green text-white rounded-lg hover:bg-whatsapp-dark transition-colors">
                      Aggiorna Password
                    </button>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Autenticazione a Due Fattori
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Aggiungi un livello extra di sicurezza
                      </p>
                    </div>
                    <button className="px-4 py-2 bg-whatsapp-green text-white rounded-lg hover:bg-whatsapp-dark transition-colors">
                      Attiva
                    </button>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Sessioni Attive</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-dark-surface rounded-lg">
                      <div className="flex items-center gap-3">
                        <Smartphone className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-sm">
                            Chrome su macOS
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Milano, Italia • Attivo ora
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-green-600 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                        Dispositivo attuale
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'data':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Gestione Dati
              </h3>
              <div className="space-y-4">
                <div className="p-6 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4">Backup Dati</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Esporta tutti i tuoi dati, automazioni e configurazioni in un file sicuro.
                  </p>
                  <button
                    onClick={handleExportData}
                    className="flex items-center gap-2 px-4 py-2 bg-whatsapp-green text-white rounded-lg hover:bg-whatsapp-dark transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Esporta Dati</span>
                  </button>
                </div>

                <div className="p-6 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4">Ripristina Dati</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Importa un backup precedentemente salvato.
                  </p>
                  <button
                    onClick={handleImportData}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Importa Backup</span>
                  </button>
                </div>

                <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <h4 className="font-medium text-red-900 dark:text-red-300 mb-2">
                    Zona Pericolosa
                  </h4>
                  <p className="text-sm text-red-700 dark:text-red-400 mb-4">
                    Le seguenti azioni sono irreversibili. Procedi con cautela.
                  </p>
                  <div className="space-y-2">
                    <button className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors">
                      Cancella Tutte le Automazioni
                    </button>
                    <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                      Elimina Account
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-whatsapp-green to-emerald-600 rounded-full flex items-center justify-center">
              <SettingsIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Impostazioni</h1>
              <p className="text-gray-500 dark:text-gray-400">
                Gestisci le tue preferenze e configurazioni
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    activeSection === section.id
                      ? 'bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white shadow-lg'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-surface'
                  }`}
                >
                  <section.icon className="w-5 h-5" />
                  <div className="text-left">
                    <p className="font-medium">{section.title}</p>
                    {activeSection === section.id && (
                      <p className="text-xs opacity-90 mt-0.5">{section.description}</p>
                    )}
                  </div>
                  {activeSection === section.id && (
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6">
              {renderSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
